import BaseButton from 'components/atoms/baseButton';
import RiskScoreTag from 'components/atoms/tags/riskScoreTag';
import classNames from 'classnames';
import SlaTag from 'components/atoms/tags/SlaTag';
import DoNotCallTag from 'components/atoms/tags/DoNotCallTag';
import EmailUnsubscribedTag from 'components/atoms/tags/EmailUnsubscribedTag';
import { isUndefined } from 'lodash';
import BaseLoader from 'components/atoms/base-loader';
import { useTranslation } from 'react-i18next';
import PcmTag from 'components/atoms/tags/PcmTag';
import EditProvider from 'infrastructure/providers/edit-provider';
import ContactAvailabilityCombinedForm from 'components/molecules/contact-availability/combined-form';
import SalesForceIntegrationTag from 'components/atoms/tags/sales-force-integration-tag';

import s from './styles.module.scss';
import PatientServiceRow from '../patient-service-row';
import PatientStatusRow from '../patient-status-row';
import { ActionDropdownButton } from '../action-dropdown-button';
import { useActionButtons } from '../../hooks';
import ConditionsRow from '../condition-row';
import Badges from '../badges';

import type {
  ICondition,
  IPatientHoldingStatus,
} from 'infrastructure/interfaces';
import type { FC, ReactElement } from 'react';

interface CombineMobileBannerProps {
  patientGuid: string;
  showHoldingStatusBadge: boolean;
  showTemporaryHoldBadge?: boolean;
  isLoading: boolean;
  holdingStatus?: IPatientHoldingStatus;
  remainingDays: number;
  birthDate: string;
  fullName: string;
  statusColor?: string;
  isShowDoNotCallTag?: boolean;
  isShowEmailTag?: boolean;
  pcmStatus?: boolean;
  showSalesForceIntegration?: boolean;
  pmpmStatus?: boolean;
  sla?: number;
  providerServiceType?: string;
  serviceDeliveryModel?: string;
  clinicName?: ReactElement | string;
  physician?: ReactElement | string;
  referralTypes?: string;
  rpmStatusObj?: Readonly<{
    label: string;
    color: string;
  }>;
  ccmStatusObj?: Readonly<{
    label: string;
    color: string;
  }>;
  riskScore?: number;
  disabledCreateInternalAdmissionForms?: boolean;
  conditions?: ICondition[];
  careAgentName?: string;
}

const CombineMobileBanner: FC<CombineMobileBannerProps> = ({
  patientGuid,
  showHoldingStatusBadge,
  showTemporaryHoldBadge,
  isLoading,
  statusColor,
  holdingStatus,
  remainingDays,
  isShowDoNotCallTag,
  isShowEmailTag,
  pcmStatus,
  showSalesForceIntegration,
  sla,
  birthDate,
  providerServiceType,
  serviceDeliveryModel,
  clinicName,
  physician,
  referralTypes,
  fullName,
  rpmStatusObj,
  ccmStatusObj,
  riskScore,
  disabledCreateInternalAdmissionForms,
  conditions,
  careAgentName,
  pmpmStatus,
}) => {
  const { t } = useTranslation();
  const { combinedActionButtons, combinedActionDropdownButtons } =
    useActionButtons({
      patientGuid,
      disabledCreateInternalAdmissionForms,
    });

  return (
    <div
      data-cy="patient-header"
      className={classNames(s.wrapper, {
        [s.corner]: showHoldingStatusBadge || showTemporaryHoldBadge,
      })}
      id="patient-banner-mob"
    >
      {isLoading ? (
        <div className={s['loading-box']}>
          <BaseLoader loading={isLoading} />
        </div>
      ) : (
        <>
          <div
            className={s['status-indicator']}
            style={{ backgroundColor: statusColor }}
          />
          <Badges
            remainingDays={remainingDays}
            showHoldingStatusBadge={showHoldingStatusBadge}
            holdingStatus={holdingStatus}
            showTemporaryHoldBadge={showTemporaryHoldBadge}
            showPmpmBadge={pmpmStatus}
          />
          <div className={s['title-wrapper']}>
            <h4 className={s.title} data-cy="patient-name">
              {fullName}
            </h4>
            <p className={s.date} data-cy="patient-birthday-date">
              {birthDate}
            </p>
            <div className={s['tags-wrapper']}>
              {!isUndefined(sla) && <SlaTag hours={sla} small />}
              {isShowDoNotCallTag && <DoNotCallTag small />}
              {isShowEmailTag && <EmailUnsubscribedTag small />}
              {pcmStatus && <PcmTag small />}
              {showSalesForceIntegration && <SalesForceIntegrationTag small />}
            </div>
          </div>
          <div className={s.divider} />
          <div
            className={classNames(s.status, {
              [s.reverse]: false,
            })}
          >
            <PatientStatusRow
              isActiveLabel
              isActiveValue
              label={t('labels.rpmStatus')}
              value={rpmStatusObj?.label ?? 'Invalid Status'}
              color={rpmStatusObj?.color}
            />
            <PatientStatusRow
              isActiveLabel
              isActiveValue
              label={t('labels.ccmStatus')}
              value={ccmStatusObj?.label ?? 'Invalid Status'}
              color={ccmStatusObj?.color}
            />
          </div>
          <div className={s.divider} />
          <div className={s['risk-score-wrapper']}>
            {!isUndefined(riskScore) && <RiskScoreTag score={riskScore} />}
          </div>
          <ConditionsRow conditions={conditions} />
          {(conditions?.length || riskScore) && <div className={s.divider} />}
          <div className={s.services}>
            <PatientServiceRow
              label={t('labels.providerServiceType')}
              value={providerServiceType}
            />
            <PatientServiceRow
              label={t('labels.serviceDeliveryModel')}
              value={serviceDeliveryModel}
            />
            <PatientServiceRow
              label={t('labels.bannerClinic')}
              value={clinicName}
            />
            <PatientServiceRow
              label={t('labels.bannerProvider')}
              value={physician}
            />
            <PatientServiceRow
              label={t('labels.bannerReferralType')}
              value={referralTypes}
            />
            <PatientServiceRow
              label={t('labels.careManager')}
              value={careAgentName}
            />
          </div>
          <div className={s['buttons-wrapper']}>
            {combinedActionDropdownButtons.map((b) => (
              <ActionDropdownButton
                key={b.key}
                options={b.options}
                label={b.label}
                dataCy={b.dataCy}
                loading={b.loading}
              />
            ))}
            {combinedActionButtons.map((btn) => (
              <BaseButton
                key={btn.key}
                label={btn.label}
                icon={<btn.Icon />}
                onClick={btn.handleClick}
                loading={btn.loading}
                type="secondary"
                dataCy={btn.dataCy}
              />
            ))}
          </div>
          <div className={s['availability-wrapper']}>
            <EditProvider>
              <ContactAvailabilityCombinedForm
                patientGuid={patientGuid}
                mode="banner"
                disableBannerEditBtn={false}
              />
            </EditProvider>
          </div>
        </>
      )}
    </div>
  );
};
export default CombineMobileBanner;
